import clsx from 'clsx'
import { useCallback, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import $type from '@tandem/ui/type.css'
import $form from '@tandem/ui/form.css'
import Spinner from '@tandem/ui/Spinner'
import Modal from '@tandem/ui/Modal'
import $modal from '@tandem/ui/Modal/styles.css'

import { useTRPC, type RouterOutputs } from '@/trpc'
import { SYSTEM_COMPANY_ID } from '@tandem/schema/permissions'

import { UserPlusIcon } from 'lucide-react'
import XMarkIcon from '@tandem/ui/icons/xmark.svg?react'
import TrashIcon from '@tandem/ui/icons/trash.svg?react'
import $anim from '@tandem/ui/anim.css'

import $table from '@tandem/ui/table.css'

import $ from './-styles.css'
import CreateCompanyForm from '@/companies/CreateCompanyForm'

type CompanySummary = RouterOutputs['company']['list'][number]

export const Route = createFileRoute('/(admin)/companies/')({
  component: CompaniesPage
})

function CompaniesPage() {
  let [showModal, setShowModal] = useState(false)

  let trpc = useTRPC()
  let { data: companies = [], isFetching: isFetchingCompanies } = useQuery(trpc.company.list.queryOptions())

  const closeModal = useCallback(() => {
    setShowModal(false)
  }, [])

  return (
    <div class={clsx('container', $.container)}>
      <h3>Компании</h3>
      <header class={$.header}>
        {companies.length > 0 && (
          <div class={$.search}>
            <input type='text' placeholder='Найти' />
          </div>
        )}
        <div class={$.controls}>
          <button
            class={$.create}
            onClick={() => {
              setShowModal(true)
            }}
          >
            <UserPlusIcon />
            Добавить компанию
          </button>
        </div>
      </header>

      {companies.length > 0 ?
        <table
          class={clsx('unstyled', $table.table, {
            [$anim.loadingOverlay]: isFetchingCompanies
          })}
        >
          <thead>
            <tr>
              <th>Название</th>
              <th>Организация</th>
              <th>Участники</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <CompanyRow key={company.id} company={company} />
            ))}
          </tbody>
        </table>
      : <div class={$.empty}>
          <p class={$type.muted}>
            {isFetchingCompanies ?
              <Spinner />
            : 'Начни с добавления компании.'}
          </p>
        </div>
      }

      <Modal open={showModal} onClose={closeModal}>
        <div class={$modal.header}>
          <h3>Добавить компанию</h3>
          <button class={clsx('plain', $modal.close)} onClick={closeModal} aria-label='Close'>
            <XMarkIcon />
          </button>
        </div>
        <CreateCompanyForm onCancel={closeModal} onSuccess={closeModal} class={$form.modal} />
      </Modal>
    </div>
  )
}

function CompanyRow({ company }: { company: CompanySummary }) {
  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let deletion = useMutation({
    ...trpc.company.delete.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(trpc.company.list.queryOptions())
      }
    })
  })

  let isSystem = company.id === SYSTEM_COMPANY_ID

  return (
    <tr key={company.id}>
      <td class={clsx($.companyName, isSystem && $.system)}>
        <Link to='/companies/$id' params={{ id: String(company.id) }} class='link'>
          {company.name}
        </Link>
      </td>
      <td>
        {company.organization ?
          <div>
            <div>{company.organization.name}</div>
            {company.organization.inn && <span class={$.meta}>ИНН: {company.organization.inn}</span>}
          </div>
        : '—'}
      </td>
      <td>
        {company.members?.length > 0 ?
          <div>
            {company.members.map((member, index) => (
              <div
                key={index}
                style={{
                  marginBottom: index < company.members.length - 1 ? '0.5rem' : 0
                }}
              >
                <div>
                  {member.first_name} {member.last_name}
                </div>
                {member.phones?.length > 0 && <div class={$.meta}>{member.phones[0].number}</div>}
                {member.emails?.length > 0 && <div class={$.meta}>{member.emails[0].address}</div>}
              </div>
            ))}
          </div>
        : '—'}
      </td>
      <td>
        <div class={$.actions}>
          {!isSystem && (
            <button
              class={clsx('plain', $type.muted)}
              onClick={() => {
                if (confirm(`Вы уверены, что хотите удалить компанию "${company.name}"?`)) {
                  deletion.mutate({ id: company.id })
                }
              }}
              disabled={deletion.isPending}
            >
              {deletion.isPending ?
                <Spinner />
              : <TrashIcon />}
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}
